import { useIsHydrated } from '@core/shared';
import { useTranslation } from 'react-i18next';
import { useRecipientChunkUpload, useSpreadsheetImport } from '../../../hooks';
import { SpreadsheetImport } from '../../organism';
import { emailListImportFields } from '../ImportEmailListPage/emailListImportFields';

interface Props {
    listId: string;
    /** Called once all appended recipients have been uploaded. */
    onComplete: () => void;
}

/**
 * Inline importer that appends more recipients to an existing (not-yet-sent-to) list. Owns its own
 * spreadsheet + upload state so the parent can reset it by remounting (changing its `key`).
 */
export const AppendRecipients = ({ listId, onComplete }: Props) => {
    const { t } = useTranslation();
    const isHydrated = useIsHydrated();
    const importer = useSpreadsheetImport(emailListImportFields);
    const upload = useRecipientChunkUpload();

    const startAppend = () => {
        const recipients = importer.buildProfiles();
        if (!recipients || recipients.length === 0) {
            return;
        }
        upload.start(listId, recipients, { onComplete });
    };

    return (
        <SpreadsheetImport
            importer={importer}
            isImporting={upload.isUploading}
            onImport={isHydrated ? startAppend : () => {}}
            importButtonText={t('EmailLists.Detail.Append.Button')}
            importTestId="email-list-append-submit"
            progressOpen={upload.isUploading}
            progressCurrent={upload.progressCurrent}
            progressTotal={upload.progressTotal}
            error={upload.error}
        />
    );
};
